import React, {
  useState,
  useCallback,
  useRef,
  useEffect,
  useMemo,
  createContext
} from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router';
import { useScreenSize } from '@core/hooks';
import Header from './Header/Header';
import useMenuPatch from './hooks/useMenuPatch';
import NavDrawer from './NavDrawer/NavDrawer';
import { useNavigation } from '../withNavLayout';

export const MenuStatus = {
  Closed: 1,
  Opened: 2,
  TemporaryOpened: 3
};

export const SideNavContext = createContext({});

export default function ({ children, toggleTheme }) {
  const scrollViewRef = useRef();
  const treeViewRef = useRef();
  const firstLoad = useRef(true);
  const isCompactRef = useRef(null);
  const currPathName = useRef(null);
  const history = useHistory();
  const { isXSmall, isLarge } = useScreenSize();
  const [patchCssClass, onMenuReady] = useMenuPatch();
  const [menuStatus, setMenuStatus] = useState(
    isLarge ? MenuStatus.Opened : MenuStatus.Closed
  );

  const { navItems } = useNavigation();

  const toggleMenu = useCallback(({ event }) => {
    setMenuStatus((prevMenuStatus) =>
      prevMenuStatus === MenuStatus.Closed
        ? MenuStatus.Opened
        : MenuStatus.Closed
    );
    event.stopPropagation();
  }, []);

  const temporaryOpenMenu = useCallback(() => {
    setMenuStatus((prevMenuStatus) =>
      prevMenuStatus === MenuStatus.Closed
        ? MenuStatus.TemporaryOpened
        : prevMenuStatus
    );
  }, []);

  const onOutsideClick = useCallback(() => {
    setMenuStatus((prevMenuStatus) =>
      prevMenuStatus !== MenuStatus.Closed && !isLarge
        ? MenuStatus.Closed
        : prevMenuStatus
    );
  }, [isLarge]);

  const onNavigationChanged = useCallback(
    ({ itemData: { path }, event, node, menuShouldBeClosed = true }) => {
      const menuStatusClosedValid =
        menuShouldBeClosed && menuStatus === MenuStatus.Closed;

      if (menuStatusClosedValid || !path || node?.selected) {
        event.preventDefault();
        return;
      }

      history.push(path);
      scrollViewRef.current.instance.scrollTo(0);

      if (!isLarge || menuStatus === MenuStatus.TemporaryOpened) {
        setMenuStatus(MenuStatus.Closed);
        event.stopPropagation();
      }
    },
    [history, menuStatus, isLarge]
  );

  const handleLinkClick = useCallback(
    ({ path, event }) => {
      onNavigationChanged({
        itemData: { path },
        event,
        menuShouldBeClosed: false
      });
    },
    [onNavigationChanged]
  );

  const compactMode = useMemo(() => {
    const isCompact = menuStatus === MenuStatus.Closed;

    isCompactRef.current = isCompact;

    return isCompact;
  }, [menuStatus]);

  const { pathname: currentPath } = useLocation();

  useEffect(() => {
    currPathName.current = currentPath;
  }, [currentPath]);

  const selectTreeViewItems = (customRef) => {
    const treeViewInst = customRef || treeViewRef.current?.instance;

    if (treeViewInst) {
      treeViewInst.selectItem(currPathName.current);
      treeViewInst.expandItem(currPathName.current);
    }
  };

  useEffect(() => {
    if (!currentPath) return;

    selectTreeViewItems();
  }, [currentPath]);

  useEffect(() => {
    const treeView = treeViewRef.current && treeViewRef.current.instance;
    if (!treeView) {
      return;
    }

    selectTreeViewItems();

    if (compactMode) {
      treeView.collapseAll();
    }
  }, [compactMode]);

  const onTreeViewReady = useCallback(
    (e) => {
      if (firstLoad.current) {
        const treeViewInst = e.component.instance();

        selectTreeViewItems(treeViewInst);
        firstLoad.current = false;

        treeViewInst.repaint();
      }

      if (!isCompactRef.current) {
        selectTreeViewItems();
      }

      onMenuReady(e);
    },
    [onMenuReady] // no more deps to prevent TreeView rerender
  );

  const { themes } = useSelector((state) => ({
    themes: state.themes
  }));

  const darkThemeEnabled = themes.darkTheme || themes.darkCompactTheme;

  return (
    <div className={'side-nav-outer-toolbar'}>
      <SideNavContext.Provider value={{ handleLinkClick }}>
        <Header
          className={'layout-header'}
          menuToggleEnabled
          toggleMenu={toggleMenu}
          toggleTheme={toggleTheme}
        />
        <NavDrawer
          treeViewRef={treeViewRef}
          compactMode={compactMode}
          darkThemeEnabled={darkThemeEnabled}
          patchCssClass={patchCssClass}
          onOutsideClick={onOutsideClick}
          isLarge={isLarge}
          isXSmall={isXSmall}
          navItems={navItems}
          onNavigationChanged={onNavigationChanged}
          temporaryOpenMenu={temporaryOpenMenu}
          onMenuReady={onTreeViewReady}
          scrollViewRef={scrollViewRef}
        >
          {children}
        </NavDrawer>
      </SideNavContext.Provider>
    </div>
  );
}
